
import React from 'react';
import { Question, Answer, QuestionTypeEnum } from '@/types/survey';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/contexts/LanguageContext';
import { HelpCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import TextInputQuestion from './TextInputQuestion';

interface QuestionItemProps {
  question: Question;
  answer: Answer;
  onChange: (id: number, value: Answer) => void;
}

const QuestionItem: React.FC<QuestionItemProps> = ({ question, answer, onChange }) => {
  const { t } = useLanguage();
  
  const renderTooltip = (text: string) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="ml-1 inline-flex cursor-help">
            <HelpCircle className="h-4 w-4 text-gray-400" />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="text-sm">{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const renderQuestionText = () => (
    <div className="mb-2">
      <Label htmlFor={`question-${question.id}`} className="text-base font-medium">
        {question.text}
        {question.tooltip && renderTooltip(question.tooltip)}
      </Label>
      {question.explanation && (
        <p className="text-sm text-gray-500 mt-1">{question.explanation}</p>
      )}
    </div>
  );

  switch (question.type) {
    case QuestionTypeEnum.TEXT:
      return (
        <div className="mb-6">
          {renderQuestionText()}
          {question.minWords ? (
            <TextInputQuestion
              question={question}
              answer={answer as string}
              onChange={(value) => onChange(question.id, value)}
            />
          ) : (
            <Textarea
              id={`question-${question.id}`}
              placeholder={question.example || t('survey.enterYourAnswer')}
              value={answer as string || ''}
              onChange={(e) => onChange(question.id, e.target.value)}
              className="w-full"
            />
          )}
        </div>
      );

    case QuestionTypeEnum.RADIO:
      return (
        <div className="mb-6">
          {renderQuestionText()}
          <RadioGroup
            value={answer as string}
            onValueChange={(value) => onChange(question.id, value)}
            className="space-y-2 mt-2"
          >
            {question.options?.map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <RadioGroupItem value={option} id={`q${question.id}-${option}`} />
                <Label htmlFor={`q${question.id}-${option}`} className="cursor-pointer">
                  {option}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      );

    case QuestionTypeEnum.SELECT:
      return (
        <div className="mb-6">
          {renderQuestionText()}
          <div className="flex flex-wrap gap-2 mt-2">
            {question.options?.map((option) => {
              const isSelected = Array.isArray(answer) && answer.includes(option);
              return (
                <Button
                  key={option}
                  type="button"
                  variant={isSelected ? "default" : "outline"}
                  onClick={() => {
                    let newValue: string[];
                    if (Array.isArray(answer)) {
                      if (isSelected) {
                        newValue = answer.filter((item) => item !== option);
                      } else {
                        newValue = [...answer, option];
                      }
                    } else {
                      newValue = [option];
                    }
                    onChange(question.id, newValue);
                  }}
                  className="mb-2"
                >
                  {option}
                </Button>
              );
            })}
          </div>
        </div>
      );

    case QuestionTypeEnum.SCALE:
      const scaleValue = typeof answer === 'number' ? [answer] : [0];
      return (
        <div className="mb-6">
          {renderQuestionText()}
          <div className="mt-4">
            <Slider
              min={0}
              max={question.scale || 10}
              step={1}
              value={scaleValue}
              onValueChange={(values) => onChange(question.id, values[0])}
            />
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span>{t('survey.low')}</span>
              <span>{t('survey.high')}</span>
            </div>
          </div>
        </div>
      );

    case QuestionTypeEnum.DROPDOWN:
      return (
        <div className="mb-6">
          {renderQuestionText()}
          <Select 
            value={answer as string} 
            onValueChange={(value) => onChange(question.id, value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('survey.selectOption')} />
            </SelectTrigger>
            <SelectContent>
              {question.options?.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case QuestionTypeEnum.LIKERT:
      return (
        <div className="mb-6">
          {renderQuestionText()}
          <div className="grid grid-cols-5 gap-2 mt-3">
            {[1, 2, 3, 4, 5].map((value) => (
              <Button
                key={value}
                type="button"
                variant={answer === value ? "default" : "outline"}
                onClick={() => onChange(question.id, value)}
                className="text-center py-2"
              >
                {value}
              </Button>
            ))}
          </div>
          <div className="flex justify-between mt-1 text-xs text-gray-500">
            <span>{t('survey.stronglyDisagree')}</span>
            <span>{t('survey.stronglyAgree')}</span>
          </div>
        </div>
      );

    case QuestionTypeEnum.BINARY:
      return (
        <div className="mb-6">
          {renderQuestionText()}
          <div className="flex gap-4 mt-2">
            <Button
              type="button"
              variant={answer === true ? "default" : "outline"}
              onClick={() => onChange(question.id, true)}
            >
              {t('survey.yes')}
            </Button>
            <Button
              type="button"
              variant={answer === false ? "default" : "outline"}
              onClick={() => onChange(question.id, false)}
            >
              {t('survey.no')}
            </Button>
          </div>
        </div>
      );

    case QuestionTypeEnum.RATING:
      return (
        <div className="mb-6">
          {renderQuestionText()}
          <div className="flex gap-2 mt-3">
            {[1, 2, 3, 4, 5].map((rating) => (
              <Button
                key={rating}
                type="button"
                variant={answer === rating ? "default" : "outline"}
                onClick={() => onChange(question.id, rating)}
                className="w-10 h-10 p-0 flex items-center justify-center"
              >
                {rating}
              </Button>
            ))}
          </div>
          <div className="flex justify-between mt-1 text-xs text-gray-500">
            <span>{t('survey.poor')}</span>
            <span>{t('survey.excellent')}</span>
          </div>
        </div>
      );

    default:
      return null;
  }
};

export default QuestionItem;
